/**
 * AddCampusDrawer — Add a new campus or migrate a campus for this corporation.
 *
 * Simplified version of CampusDrawer from the CampusesPage:
 *   - No corporation dropdown — auto-uses current corporateId prop
 *   - Same New / Migrate tab structure
 *   - Same field set: name, country, address, city, state, zip, timezone, plan expiry
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getCountryData, getStatesByCountry, getZipRegexByCountry } from '../../../../../libs.jsx';
import HeaderInfoTip from '../../../../../components/v2/HeaderInfoTip.jsx';
import '../../../../../components/v2/drawer-tabs.css';
import Drawer from '../../../../../components/v2/Drawer.jsx';

const AddCampusDrawer = ({ open, onClose, onSave, corporateId, corporateName, timezones }) => {
  const drawerRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'migrate'

  // Form state
  const [campusName, setCampusName] = useState('');
  const [saving, setSaving] = useState(false);

  // New Campus fields
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [timezoneId, setTimezoneId] = useState('');
  const [planExpire, setPlanExpire] = useState('');

  // Migrate Campus fields
  const [campusGuid, setCampusGuid] = useState('');

  // Reset form on open
  useEffect(() => {
    if (open) {
      setActiveTab('new');
      setCampusName('');
      setCountry('');
      setAddress('');
      setCity('');
      setState('');
      setZipcode('');
      setTimezoneId('');
      setPlanExpire('');
      setCampusGuid('');
      setSaving(false);
    }
  }, [open]);

  // Focus first input on open
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Reset state/zip when country changes
  useEffect(() => {
    setState('');
    setZipcode('');
  }, [country]);

  // Validation
  const namePattern = /^[0-9a-zA-Z_ \-]+$/;
  const guidPattern = /^[0-9a-zA-Z_ \-]+$/;

  const isNameValid = campusName.trim() && namePattern.test(campusName.trim());
  const isGuidValid = campusGuid.trim() && guidPattern.test(campusGuid.trim());

  const zipRegex = country ? getZipRegexByCountry(country) : '';
  const isZipValid = zipcode.trim() && (!zipRegex || new RegExp(`^${zipRegex}$`).test(zipcode.trim()));

  const isNewFormValid = isNameValid && country && address.trim() && city.trim() && state && (zipRegex ? isZipValid : zipcode.trim()) && timezoneId && planExpire;
  const isMigrateFormValid = isGuidValid && timezoneId;

  const isFormValid = activeTab === 'new' ? isNewFormValid : isMigrateFormValid;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid) return;
      setSaving(true);

      const formData = activeTab === 'new'
        ? {
          campus_name: campusName.trim(),
          corporate_id: parseInt(corporateId, 10),
          plan_expire: `${planExpire} 00:00:00`,
          campus_guid: '',
          address: address.trim(),
          city: city.trim(),
          state,
          zipcode: zipcode.trim(),
          timezone_id: parseInt(timezoneId, 10),
          country,
          migrate: false,
        }
        : {
          campus_name: '',
          corporate_id: parseInt(corporateId, 10),
          plan_expire: '',
          campus_guid: campusGuid.trim(),
          address: '',
          city: '',
          state: '',
          zipcode: '',
          timezone_id: parseInt(timezoneId, 10),
          country: '',
          migrate: true,
        };

      onSave(formData).finally(() => setSaving(false));
    },
    [isFormValid, activeTab, corporateId, campusName, country, address, city, state, zipcode, timezoneId, planExpire, campusGuid, onSave],
  );

  // Data helpers
  const countries = getCountryData();
  const states = country ? (getStatesByCountry(country) || []) : [];

  const sortedTimezones = [...(timezones || [])].sort((a, b) =>
    (a.timezone_name || '').localeCompare(b.timezone_name || '', undefined, { sensitivity: 'base' }),
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      panelRef={drawerRef}
      ariaLabel="Add Campus"
      headerStart={(
        <div className="cp-drawer-title-row">
          <h2 className="dp-drawer__title">Add Campus</h2>
          <HeaderInfoTip
            label="About 5.0 and 4.0 campuses"
            spaced
            title="5.0 Campus"
            body="New campus created solely on the 5.0 platform. Does not require setup in the NOC and cannot be downgraded to 4.0 once created."
            sections={[
              { heading: '4.0 Campus', body: 'Backward-compatible campus running on the 4.0 platform. Must first be set up in the NOC and then migrated over using its GUID.' },
            ]}
          />
        </div>
      )}
      subheader={(
        <div className="dp-drawer-tabs">
          <button
            type="button"
            className={`dp-drawer-tab ${activeTab === 'new' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            5.0 Campus
          </button>
          <button
            type="button"
            className={`dp-drawer-tab ${activeTab === 'migrate' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('migrate')}
          >
            4.0 Campus
          </button>
        </div>
      )}
      rawBody
    >
      {/* Form */}
      <form onSubmit={handleSubmit} className="dp-drawer__body">
        {/* Corporation (read-only) */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Corporation</label>
          <input
            type="text"
            className="dp-drawer__input"
            value={corporateName || ''}
            disabled
          />
        </div>

        {/* Campus Name (5.0 only) */}
        {activeTab === 'new' ? (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">
              Campus Name <span className="dp-drawer__required">*</span>
            </label>
            <input
              type="text"
              value={campusName}
              onChange={(e) => setCampusName(e.target.value)}
              placeholder="Campus name"
              className={`dp-drawer__input ${campusName && !isNameValid ? 'is-invalid' : ''}`}
              required
            />
            {campusName && !isNameValid ? (
              <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
            ) : null}
          </div>
        ) : null}

        {/* === NEW CAMPUS TAB === */}
        {activeTab === 'new' ? (
          <>
            {/* Country */}
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Country <span className="dp-drawer__required">*</span>
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="dp-drawer__select"
                required
              >
                <option value="">Select country...</option>
                {countries.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>

            {/* Street Address */}
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Street Address <span className="dp-drawer__required">*</span>
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street address"
                className="dp-drawer__input"
                required
              />
            </div>

            {/* City + State + Zip */}
            <div className="crpp-drawer-address-row">
              <div className="dp-drawer__field" style={{ flex: '2' }}>
                <label className="dp-drawer__label">
                  City <span className="dp-drawer__required">*</span>
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="dp-drawer__input"
                  required
                />
              </div>
              <div className="dp-drawer__field" style={{ flex: '1' }}>
                <label className="dp-drawer__label">
                  State <span className="dp-drawer__required">*</span>
                </label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="dp-drawer__select"
                  required
                  disabled={!country}
                >
                  <option value="">...</option>
                  {states.map(([code]) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div className="dp-drawer__field" style={{ flex: '1' }}>
                <label className="dp-drawer__label">
                  Zip <span className="dp-drawer__required">*</span>
                </label>
                <input
                  type="text"
                  value={zipcode}
                  onChange={(e) => setZipcode(e.target.value)}
                  placeholder="Zip"
                  className={`dp-drawer__input ${zipcode && !isZipValid ? 'is-invalid' : ''}`}
                  required
                />
              </div>
            </div>

            {/* Timezone */}
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Timezone <span className="dp-drawer__required">*</span>
              </label>
              <select
                value={timezoneId}
                onChange={(e) => setTimezoneId(e.target.value)}
                className="dp-drawer__select"
                required
              >
                <option value="">Select timezone...</option>
                {sortedTimezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_id}>
                    {tz.timezone_name} ({tz.timezone})
                  </option>
                ))}
              </select>
            </div>

            {/* Service Plan Expiry */}
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Service Plan Expiry <span className="dp-drawer__required">*</span>
              </label>
              <input
                type="date"
                value={planExpire}
                onChange={(e) => setPlanExpire(e.target.value)}
                className="dp-drawer__input"
                required
              />
            </div>
          </>
        ) : null}

        {/* === MIGRATE CAMPUS TAB === */}
        {activeTab === 'migrate' ? (
          <>
            {/* Campus 2.0 GUID */}
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Campus GUID from NOC <span className="dp-drawer__required">*</span>
              </label>
              <input
                type="text"
                value={campusGuid}
                onChange={(e) => setCampusGuid(e.target.value)}
                placeholder="Campus 2.0 GUID"
                className={`dp-drawer__input ${campusGuid && !isGuidValid ? 'is-invalid' : ''}`}
                required
              />
              {campusGuid && !isGuidValid ? (
                <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
              ) : null}
            </div>

            {/* Timezone */}
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Timezone <span className="dp-drawer__required">*</span>
              </label>
              <select
                value={timezoneId}
                onChange={(e) => setTimezoneId(e.target.value)}
                className="dp-drawer__select"
                required
              >
                <option value="">Select timezone...</option>
                {sortedTimezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_id}>
                    {tz.timezone_name} ({tz.timezone})
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : null}

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button
            type="button"
            onClick={onClose}
            className="dp-btn dp-btn--secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || saving}
            className="dp-btn dp-btn--primary"
          >
            {saving ? 'Adding...' : activeTab === 'new' ? 'Add Campus' : 'Migrate Campus'}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default AddCampusDrawer;
